import React, { useState } from 'react';
import styled from '@emotion/styled';
import { gql, useMutation, useQuery } from '@apollo/client';
import DashboardAdminView from '../components/DashboardAdminView';
import {
  DashboardAdmin as DashboardAdminData,
  DashboardDraft as DashboardDraftData,
} from '../models/dashboard';

export const DASHBOARD_ADMIN_QUERY = gql`
  query DashboardAdmin($companyId: ID) {
    dashboardAdmin(companyId: $companyId) {
      companies {
        id
        name
      }
      company {
        id
        name
      }
      publishedThrough
      publishedBy
      publishedAt
      weeks {
        weekStart
        weekEnd
        status
        canPublish
        canRepublish
        canUnpublish
      }
    }
  }
`;

export const DASHBOARD_DRAFT_QUERY = gql`
  query DashboardDraft($companyId: ID!, $weekStart: String!) {
    dashboardDraft(companyId: $companyId, weekStart: $weekStart) {
      companyId
      weekStart
      weekEnd
      summary {
        average
        reporting
        total
        top {
          name
          score
        }
        lowest {
          name
          score
        }
      }
      locations {
        locationId
        name
        offline
        score
      }
      checks {
        blocking
        warnings
        items {
          level
          code
          message
          locationId
        }
      }
    }
  }
`;

export const PUBLISH_DASHBOARD_WEEK_MUTATION = gql`
  mutation PublishDashboardWeek(
    $companyId: ID!
    $weekStart: String!
    $acknowledgeWarnings: Boolean
  ) {
    publishDashboardWeek(
      companyId: $companyId
      weekStart: $weekStart
      acknowledgeWarnings: $acknowledgeWarnings
    ) {
      companies {
        id
        name
      }
      company {
        id
        name
      }
      publishedThrough
      publishedBy
      publishedAt
      weeks {
        weekStart
        weekEnd
        status
        canPublish
        canRepublish
        canUnpublish
      }
    }
  }
`;

export const UNPUBLISH_DASHBOARD_WEEK_MUTATION = gql`
  mutation UnpublishDashboardWeek($companyId: ID!, $weekStart: String!) {
    unpublishDashboardWeek(companyId: $companyId, weekStart: $weekStart) {
      companies {
        id
        name
      }
      company {
        id
        name
      }
      publishedThrough
      publishedBy
      publishedAt
      weeks {
        weekStart
        weekEnd
        status
        canPublish
        canRepublish
        canUnpublish
      }
    }
  }
`;

export const DashboardAdmin = () => {
  const [selectedCompanyId, setSelectedCompanyId] =
    useState<string | undefined>();
  const [reviewWeekStart, setReviewWeekStart] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishingWeek, setPublishingWeek] = useState<string | null>(null);
  const [unpublishingWeek, setUnpublishingWeek] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<{
    dashboardAdmin: DashboardAdminData;
  }>(DASHBOARD_ADMIN_QUERY, {
    variables: { companyId: selectedCompanyId },
    fetchPolicy: 'cache-and-network',
  });

  const activeCompanyId =
    selectedCompanyId ||
    data?.dashboardAdmin?.company?.id ||
    data?.dashboardAdmin?.companies?.[0]?.id;

  const { data: draftData, loading: loadingDraft } = useQuery<{
    dashboardDraft: DashboardDraftData;
  }>(DASHBOARD_DRAFT_QUERY, {
    skip: !activeCompanyId || !reviewWeekStart,
    variables: {
      companyId: activeCompanyId,
      weekStart: reviewWeekStart || '',
    },
    fetchPolicy: 'network-only',
  });

  const [publishWeek] = useMutation(PUBLISH_DASHBOARD_WEEK_MUTATION);
  const [unpublishWeek] = useMutation(UNPUBLISH_DASHBOARD_WEEK_MUTATION);

  if (loading && !data) {
    return <StatusMessage>Loading publishing admin…</StatusMessage>;
  }

  if (error) {
    return (
      <ErrorMessage>
        Error loading dashboard admin: {error.message}
      </ErrorMessage>
    );
  }

  if (!data?.dashboardAdmin) {
    return <StatusMessage>No dashboard admin data available.</StatusMessage>;
  }

  const handleSelectCompany = (id: string) => {
    setSelectedCompanyId(id);
    setReviewWeekStart(null);
    setActionError(null);
  };

  const handlePublish = async (
    weekStart: string,
    acknowledgeWarnings = false
  ) => {
    if (!activeCompanyId) return;
    setActionError(null);
    setPublishingWeek(weekStart);
    try {
      await publishWeek({
        variables: {
          companyId: activeCompanyId,
          weekStart,
          acknowledgeWarnings,
        },
      });
      setReviewWeekStart(null);
      await refetch();
    } catch (err: any) {
      setActionError(err.message || 'Failed to publish week.');
    } finally {
      setPublishingWeek(null);
    }
  };

  const handleUnpublish = async (weekStart: string) => {
    if (!activeCompanyId) return;
    setActionError(null);
    setUnpublishingWeek(weekStart);
    try {
      await unpublishWeek({
        variables: {
          companyId: activeCompanyId,
          weekStart,
        },
      });
      await refetch();
    } catch (err: any) {
      setActionError(err.message || 'Failed to unpublish week.');
    } finally {
      setUnpublishingWeek(null);
    }
  };

  return (
    <div>
      {actionError && (
        <ErrorToast onClick={() => setActionError(null)}>
          <span>{actionError}</span>
          <button type="button" aria-label="Dismiss error">
            ✕
          </button>
        </ErrorToast>
      )}

      <DashboardAdminView
        admin={data.dashboardAdmin}
        selectedCompanyId={activeCompanyId}
        onSelectCompany={handleSelectCompany}
        draft={draftData?.dashboardDraft || null}
        loadingDraft={loadingDraft}
        onReviewDraft={setReviewWeekStart}
        onCloseDraft={() => setReviewWeekStart(null)}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        publishingWeek={publishingWeek}
        unpublishingWeek={unpublishingWeek}
      />
    </div>
  );
};

export default DashboardAdmin;

const StatusMessage = styled.div`
  max-width: 600px;
  margin: 100px auto;
  text-align: center;
  font-size: 15px;
  color: #6e757c;
`;

const ErrorMessage = styled.div`
  max-width: 600px;
  margin: 100px auto;
  text-align: center;
  font-size: 15px;
  color: #ba1a1a;
  background: #fdf2f2;
  border: 1px solid #f8b4b4;
  padding: 16px 20px;
  border-radius: 8px;
`;

const ErrorToast = styled.div`
  max-width: 912px;
  margin: 16px auto -12px;
  padding: 12px 18px;
  background: #fdf2f2;
  border: 1px solid #f8b4b4;
  border-radius: 8px;
  color: #9b1c1c;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;

  button {
    background: none;
    border: none;
    font-size: 14px;
    color: #9b1c1c;
    cursor: pointer;
  }
`;
